import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

import { EDITORIAL_URL_BASE, GREATEST } from 'editorial/enums/constants';
import { IArticle } from 'editorial/types/Editorial';
import { Image } from 'shared/components/Image';
import { variableFontRegular } from 'styles/theme/text';
import { usePutBackHeaderOnPageChange } from 'shared/hooks/usePutBackHeaderOnPageChange';
import { useToggleHeaderOnScrollEvent } from 'shared/hooks/useToggleHeaderOnScrollEvent';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { breakpoints } from 'styles/theme/breakpoints';
import { t } from 'localization/utils/t';

const TP = 'editorial.components.EditorialLandingPage';

export const EditorialLandingPage = ({
  articles,
  isResourcePage,
}: {
  articles: Array<IArticle>;
  isResourcePage?: boolean;
}) => {
  useToggleHeaderOnScrollEvent();
  usePutBackHeaderOnPageChange();

  let defaultTitle = t(`${TP}.defaultTitle`, 'Exploring Sneakers, Fashion, Culture and More ');
  let SEODescription = t(
    `${TP}.seoDescription`,
    'Editorial features covering the greatest sneakers, fashion, culture and more from GOAT.',
  );

  if (isResourcePage) {
    defaultTitle = t(
      `${TP}.resourceTitle`,
      'Explore educational resources covering sneakers, fashion, culture and more. From brand histories to iconic silhouettes. Only on GOAT.',
    );
    SEODescription = t(
      `${TP}.resourceSeoDescription`,
      'Guides to Sneaker Releases, Iconic Brands & More ',
    );
  }

  const { pageTitle } = usePageMetadata({ defaultTitle });

  return (
    <>
      <Head>
        <title key="title">{pageTitle}</title>
        <meta name="description" content={SEODescription} key="description" />
        <meta name="twitter:description" content={SEODescription} />
        <meta name="twitter:title" content={pageTitle} />
        <meta property="og:description" content={SEODescription} key="og:description" />
        <meta property="og:title" content={pageTitle} />
      </Head>
      <EditorialLandingPage.Wrapper data-qa="editorial_landing_page">
        {articles.map(({ coverPicturePortrait, description, slug, subject, status, category }) => {
          const title =
            category === GREATEST ? t(`${TP}.title`, `GREATEST: {subject}`, { subject }) : subject;
          return (
            // All articles will respect "editorials/{slug}" regardless if SEO resource or not.
            <Link
              href={`${EDITORIAL_URL_BASE}/${slug}`}
              key={slug}
              passHref
              data-qa="editorial_article_link"
            >
              <EditorialLandingPage.Image
                isResourcePage={isResourcePage}
                src={coverPicturePortrait?.url}
                alt={subject}
                data-qa="editorial_article_image"
                sizes={`(max-width: ${breakpoints.mediumSmall}px) 87vw, (max-width: ${breakpoints.medium}px) 45vw, 25vw`}
              />
              {status === 'unpublished' && (
                <EditorialLandingPage.PreviewBadge>
                  <EditorialLandingPage.Badge data-qa="preview_badge">
                    {t(`${TP}.preview`, 'PREVIEW')}
                  </EditorialLandingPage.Badge>
                </EditorialLandingPage.PreviewBadge>
              )}
              <EditorialLandingPage.Title data-qa="editorial_article_title">
                {title}
              </EditorialLandingPage.Title>
              <EditorialLandingPage.Blurb data-qa="editorial_article_description">
                {description}
              </EditorialLandingPage.Blurb>
            </Link>
          );
        })}
      </EditorialLandingPage.Wrapper>
    </>
  );
};

EditorialLandingPage.Wrapper = styled.div`
  width: 100%;
  padding: 24px 24px;
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  grid-row-gap: 40px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding: 60px 90px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-column-gap: 30px;
    grid-row-gap: 100px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-column-gap: 90px;
  }
`;

EditorialLandingPage.Badge = styled.div`
  background-color: ${({ theme }) => theme.colors.red};
  border-color: ${({ theme }) => theme.colors.red};
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  color: ${({ theme }) => theme.colors.white};
  display: inline-block;
  line-height: 16px;
  text-align: center;
  text-transform: capitalize;
  padding: 2px 11px;
`;

EditorialLandingPage.PreviewBadge = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
`;

EditorialLandingPage.Title = styled.h2`
  ${variableFontRegular({ size: '16px', height: '18px', spacing: '0.32' })};
  text-transform: uppercase;
  margin: 20px 0 15px;
  font-weight: 400;
`;

EditorialLandingPage.Blurb = styled.p``;

EditorialLandingPage.Image = styled(Image)<{ isResourcePage: boolean }>`
  border: ${({ isResourcePage }) => (isResourcePage ? 'black solid 1px' : '')};
`;
